<script setup lang="ts">
import { UserIcon } from '@heroicons/vue/outline'

import {
    accountLinksPersonal,
    AccountNavigationStore,
    getHiddenNavigationSections,
    navigateTo,
    navigationItemsPersonal,
    PersonalAccountPage,
    useAccountNavigationState,
} from '../../../utilities/account/useAccountNavigationPersonal'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { computed, onMounted, ref, watch } from 'vue'
import { useForm } from 'vee-validate'
import { getPersonalAccountValidationSchema } from '../../../utilities/account/useAccountFormPersonal'
import { isEcspComplianceProfileInformationMissingForInvestment } from '../../../utilities/account/useEcspRegulatory'
import { getPlatformSettings } from '../../../utilities/usePlatformSettings'
import { isEsacNaturalComplianceProfileComplete } from '../../wizard/ESAC/form/forms'
import AccountNavigation from '../AccountNavigation.vue'
import { showOrdersLink, showWalletLink } from '../../../utilities/useLinks'
import { i18n } from '../../../localization'

const navigationState: AccountNavigationStore = useAccountNavigationState() as AccountNavigationStore
const personalAccountState = usePersonalAccountDataState()
const person = personalAccountState.naturalProfile

const formErrors = ref({})
const complianceProfileComplete = ref(false)

onMounted(() => {
    setFormErrors()
    updateComplianceProfileIsComplete()
})
//
// const urlParams = new URLSearchParams(window.location.search)
// const pageLink = urlParams.get('page')
//
// if (pageLink) {
//     navigationState.page = pageLink
// }

function setFormErrors() {
    Object.keys(getPersonalAccountValidationSchema(true)).forEach(async (formSchema) => {
        const currentAccountValidationSchema = getPersonalAccountValidationSchema(true)[formSchema]

        const form = useForm({
            validationSchema: currentAccountValidationSchema,
            initialValues: { account: person },
            validateOnMount: true,
        })
        const validation = await form.validate()
        formErrors.value[formSchema] = validation
    })
}

function hasPageErrors(page: PersonalAccountPage) {
    if (page === 'Verification') {
        return !personalAccountState.isVerified
    }
    if (page === 'Signatures') {
        return !!personalAccountState.naturalProfile.signatures.find((signature) => signature.signed_at === null)
    }
    if (page === 'Compliance Profile') {
        return !complianceProfileComplete.value
    }

    return formErrors.value[page] && !formErrors.value[page].valid
}

async function updateComplianceProfileIsComplete() {
    if (getPlatformSettings().compliance_profile === 'esac') {
        complianceProfileComplete.value = await isEsacNaturalComplianceProfileComplete()
    } else {
        complianceProfileComplete.value = !(await isEcspComplianceProfileInformationMissingForInvestment())
    }
}

watch(
    () => personalAccountState.saving,
    (val) => {
        updateComplianceProfileIsComplete()
        if (!val) setFormErrors()
    }
)

const shownNavigationItems = computed(() => {
    return navigationItemsPersonal.map((item) => {
        if (item.hasOwnProperty('children')) {
            item.children = item.children.filter((child) => {
                return !getHiddenNavigationSections().includes(child.component)
            })
        }
        return item
    })
})

const shownAccountLinks = computed(() => {
    return accountLinksPersonal.map((item) => {
        if (item.hasOwnProperty('children')) {
            item.children = item.children.filter((child) => {
                // TODO: Fix this check
                if (!showWalletLink() && child.name === i18n.global.t('account_links.wallet')) {
                    return false
                }
                // TODO: Fix this check too :=)
                if (!showOrdersLink() && child.name === i18n.global.t('account_links.orders')) {
                    return false
                }
                return true
            })
        }
        return item
    })
})

const showAccountLinksPersonal = computed(() => {
    return showWalletLink() || showOrdersLink()
})

const emit = defineEmits(['navigate'])
</script>

<template>
    <div class="flex flex-shrink-0 items-center space-x-4 px-4 pb-4">
        <div class="flex items-center justify-center rounded-full border-2 border-gray-300 bg-white p-3">
            <UserIcon class="border-gray-150 w-6" />
        </div>
        <div>
            <div class="text-xs font-bold uppercase text-gray-600">{{ $t('account.my_profile') }}</div>
            <div class="text-md uppercase">
                {{ personalAccountState.naturalProfile.first_name }} {{ personalAccountState.naturalProfile.last_name }}
            </div>
        </div>
    </div>

    <div class="border-gray-150 flex flex-grow flex-col overflow-y-auto rounded-lg border bg-white py-4 shadow-md">
        <div class="lex flex-grow flex-col">
            <nav class="flex-1 space-y-1 bg-white px-2" aria-label="Sidebar">
                <AccountNavigation
                    :navigation-items="shownNavigationItems"
                    :has-page-errors="hasPageErrors"
                    :current-page="navigationState.page"
                    :navigate-to="navigateTo"
                    @navigate="emit('navigate')" />
            </nav>
        </div>
    </div>

    <div
        v-if="showAccountLinksPersonal"
        class="border-gray-150 mt-7 flex flex-grow flex-col overflow-y-auto rounded-lg border bg-white pb-4 pt-2 shadow-md">
        <div class="lex flex-grow flex-col">
            <nav class="flex-1 space-y-1 bg-white px-2" aria-label="Sidebar">
                <AccountNavigation
                    :navigation-items="shownAccountLinks"
                    :has-page-errors="hasPageErrors"
                    :current-page="navigationState.page"
                    :navigate-to="navigateTo" />
            </nav>
        </div>
    </div>
</template>

<style scoped></style>
